import { util } from "fabric";
import { Vector3D } from "../lod2";

class CalcAreaUtils {

    reverseCalculateArea(calcArea, plot) {
        const scale = plot.refScale;
        const { originX, originY } = calcArea;
        const { lengthU, lengthV, angle, vectorU, vectorV } = this.getCalcAreaBaseProperties2D(calcArea, plot);
        const x = originX / scale;
        const y = originY / scale * -1;
        const width = lengthU / scale;
        // height === 0 if area is verical
        const height = lengthV / scale;
        return { x, y, width, height, angle, vectorU, vectorV };
    };

    calculateArea(rect, plot) {
        const scale = plot.refScale;
        const { x, y } = rect.aCoords.bl;
        const originX = parseFloat((x * scale).toFixed(2));
        const originY = parseFloat((y * -1 * scale).toFixed(2));
        const lengthU = parseFloat((Math.abs(rect.width) * scale * rect.scaleX).toFixed(2));
        const lengthV = parseFloat((Math.abs(rect.height) * scale * rect.scaleY).toFixed(2));
        // rect.angle is clockwise
        const rotationcounterClockwiseRadians = ((360 - rect.angle) % 360) * Math.PI / 180;
        const vectorUNonRotated = new Vector3D(lengthU, 0, 0);
        const vectorVNonRotated = new Vector3D(0, lengthV, 0);
        const vectorU = vectorUNonRotated.rotateAroundZ(rotationcounterClockwiseRadians);
        const vectorV = vectorVNonRotated.rotateAroundZ(rotationcounterClockwiseRadians);
        const uaxisX = vectorU.x;
        const uaxisY = vectorU.y;
        const vaxisX = vectorV.x;
        const vaxisY = vectorV.y;
        return {
            originX,
            originY,
            uaxisX,
            uaxisY,
            vaxisX,
            vaxisY,
            calculation: true,
            dumping: false,
        };
    };

    calculatePathPoints = (path, plot) => {
        const reverseTransform = util.invertTransform(path.originalTransformMatrix);
        const newTransform = path.calcTransformMatrix();
        const polygons = plot.pathFromSvg(path);
        return polygons.map(polygon => polygon.map(point => {
            const transformed1 = util.transformPoint(point, reverseTransform);
            const transformed2 = util.transformPoint(transformed1, newTransform);
            return transformed2;
        }));
    };

    calculateAreaFromGroup(group, rect, boundary, plot) {
        const scale = plot.refScale;
        const isVertical = this.isVertical(group.sourceArea);
        const { x, y } = isVertical ? group.aCoords.tl : group.aCoords.bl;
        const originX = parseFloat((x * scale).toFixed(2));
        const originY = parseFloat((y * -1 * scale).toFixed(2));
        const lengthU = parseFloat((Math.abs(rect.width) * scale * group.scaleX).toFixed(2));
        const lengthV = parseFloat((Math.abs(rect.height) * scale * group.scaleY).toFixed(2));
        // rect.angle is clockwise
        const rotationcounterClockwiseRadians = ((360 - group.angle) % 360) * Math.PI / 180;
        const vectorUNonRotated = new Vector3D(lengthU, 0, 0);
        const vectorVNonRotated = new Vector3D(0, lengthV, 0);
        const vectorU = vectorUNonRotated.rotateAroundZ(rotationcounterClockwiseRadians);
        const vectorV = vectorVNonRotated.rotateAroundZ(rotationcounterClockwiseRadians);
        const uaxisX = vectorU.x;
        const uaxisY = vectorU.y;
        const vaxisX = vectorV.x;
        const vaxisY = vectorV.y;

        let boundaryPoints = undefined;
        if(boundary) {
            const polygonPointsInGroup = this.calculatePathPoints(boundary, plot);
            boundaryPoints = polygonPointsInGroup.map(path => path.map(p => ({x: p.x * scale, y: p.y * -1 * scale})));
        }

        return {
            originX,
            originY,
            uaxisX,
            uaxisY,
            vaxisX,
            vaxisY,
            calculation: true,
            dumping: false,
            boundary: boundaryPoints,
        };
    };

    calculateSliceImageProperties(calcArea, plot, image) {
        const { originX, originY } = calcArea;
        const { lengthU, lengthV, angle, angleCounterclockwiseRadians } = this.getCalcAreaBaseProperties2D(calcArea);
        const left = 0 + (originX / plot.refScale);
        const top = 0 - (originY / plot.refScale);
        const scaleX = lengthU / plot.refScale / image.width;
        const scaleY = lengthV / plot.refScale / image.height;
        return { left, top, scaleX, scaleY, angle, angleCounterclockwiseRadians };
    }

    getCalcAreaBaseProperties2D(calcArea) {
        const { uaxisX, uaxisY, vaxisX, vaxisY } = calcArea;
        const vectorU = new Vector3D(uaxisX, uaxisY, 0);
        const vectorV = new Vector3D(vaxisX, vaxisY, 0);
        const lengthU = vectorU.length();
        const lengthV = vectorV.length();
        const angleCounterclockwiseRadians = Vector3D.angle2D(new Vector3D(1, 0, 0), vectorU);
        // clockwise degrees
        const angle = (360 - (angleCounterclockwiseRadians / Math.PI * 180)) % 360;
        return { vectorU, vectorV, lengthU, lengthV, angle, angleCounterclockwiseRadians };
    }

    calculateInclinationRadians(calcArea) {
        const vAxisVector = new Vector3D(calcArea.vaxisX, calcArea.vaxisY, calcArea.vaxisZ);
        const vAxisFlatVector = new Vector3D(calcArea.vaxisX, calcArea.vaxisY, 0);
        const inclinationRadians = Vector3D.angle3D(vAxisFlatVector, vAxisVector);
        return inclinationRadians;
    };

    calculateInclination(calcArea) {
        if (calcArea.vaxisX === 0 && calcArea.vaxisY === 0) {// if wall
            return 90;
        }
        const inclinationRadians = this.calculateInclinationRadians(calcArea);
        const inclination = (inclinationRadians / Math.PI * 180) % 360;
        return Math.round(inclination * 100) / 100;
    };

    // first "Areal-N" not yet used by one of the given areas
    nextAreaName(calcAreas, usedNames = []) {
        const presentNames = [...calcAreas.map(ca => ca.name), ...usedNames];
        let nameIndex = 1;
        while (presentNames.includes(`Areal-${nameIndex}`)) {
            nameIndex++;
        }
        return `Areal-${nameIndex}`;
    }

    isVertical(calcArea) {
        return calcArea.vaxisX === 0 && calcArea.vaxisY === 0;
    }

    isHorizontal(calcArea) {
        return calcArea.vaxisZ === 0 && calcArea.uaxisZ === 0;
    }

    isPointUnderPlane(calcArea, P) {
        const origin = new Vector3D(calcArea.originX, calcArea.originY, calcArea.originZ);
        const uAxis = new Vector3D(calcArea.uaxisX, calcArea.uaxisY, calcArea.uaxisZ);
        const vAxis = new Vector3D(calcArea.vaxisX, calcArea.vaxisY, calcArea.vaxisZ);
        // normal vector
        const n = uAxis.cross(vAxis);
        // d for plane equation: a*x + b*y + c*z + d = 0
        const d = -(n.x * origin.x + n.y * origin.y + n.z * origin.z);
        // Evaluate the plane equation for point P
        const val = n.x * P.x + n.y * P.y + n.z * P.z + d;
        // If normal points upward (n.z > 0), under means val < 0
        // If normal points downward (n.z < 0), under means val > 0
        const isUnder = n.z >= 0 ? val < 0 : val > 0;
        return isUnder;
    }

    isPointBetweenAreaEdges(calcArea, P) {
        const uAxis = new Vector3D(calcArea.uaxisX, calcArea.uaxisY, calcArea.uaxisZ);
        const vAxis = new Vector3D(calcArea.vaxisX, calcArea.vaxisY, calcArea.vaxisZ);
        const zRotationAngleRadians = uAxis.zRotationAngle();

        const movedToOriginX = P.x - calcArea.originX;
        const movedToOriginY = P.y - calcArea.originY;

        const rotatedAroundZX =
            movedToOriginX * Math.cos(-zRotationAngleRadians) -
            movedToOriginY * Math.sin(-zRotationAngleRadians);
        const rotatedAroundZY =
            movedToOriginX * Math.sin(-zRotationAngleRadians) +
            movedToOriginY * Math.cos(-zRotationAngleRadians);

        const lengthV2D = Math.hypot(calcArea.vaxisX, calcArea.vaxisY);
        const localX = rotatedAroundZX;
        const localY = vAxis.length() - (rotatedAroundZY * vAxis.length() / lengthV2D);

        return localX > 0 && localX < uAxis.length() && localY > 0 && localY < vAxis.length();
    }

    calculateSliceOffsetVector(calcArea, slice, sliceDist) {
        const uAxis = new Vector3D(calcArea.uaxisX, calcArea.uaxisY, calcArea.uaxisZ);
        const vAxis = new Vector3D(calcArea.vaxisX, calcArea.vaxisY, calcArea.vaxisZ);
        const direction = uAxis.cross(vAxis);
        const unit = direction.scale(1/direction.length());
        const scaled = unit.scale(slice * sliceDist);
        return this.isVertical(calcArea) ? scaled.scale(-1) : scaled;
    }
}
const calcAreaUtils = new CalcAreaUtils();
export default calcAreaUtils;